import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, collection, query, where, getDocs, limit, Timestamp, addDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { getListingPath, getListingPublicUrl } from '../lib/listingPaths';
import { formatListingLocationLine } from '../lib/locationRegions';
import { looksLikeHtml } from '../lib/content';
import { applyPageSeo, absoluteUrl, stripHtmlForMeta } from '../lib/seo';
import {
  calculateStayBreakdown,
  formatPriceRuleLabel,
  getListingPriceDisplay,
  getNightlyPriceCents,
  sortPriceRules,
  toDateStr,
} from '../lib/pricing';
import { useAuth } from '../contexts/AuthContext';
import type { Listing } from '@shared/types';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
// AdvancedMarkerElement used when mapId is set to avoid Marker deprecation warning
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toast from 'react-hot-toast';

const MAP_LIBRARIES: [] = [];

/** Root `/slug` must not capture static app routes if the router ever matches them here */
const RESERVED_LISTING_SLUGS = new Set([
  'search',
  'find-property',
  'listing',
  'login',
  'register',
  'account',
  'bookings',
  'admin',
  'payfast-checkout',
  'booking',
  'p',
]);

export default function ListingDetail() {
  const params = useParams<{ idOrSlug?: string; slug?: string; listingSlug?: string }>();
  const routeSegment =
    params.idOrSlug || params.slug || params.listingSlug || undefined;
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState(2);
  const [availability, setAvailability] = useState<any>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [mobileGalleryOpen, setMobileGalleryOpen] = useState(false);
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryEmail, setEnquiryEmail] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [sendingEnquiry, setSendingEnquiry] = useState(false);

  // Always load the listing from Firestore so admin price/content edits show on the
  // public detail page immediately (browse cards use a separate public cache doc).
  const {
    data: listing,
    isLoading: listingLoading,
    isFetched: listingFetched,
  } = useQuery({
    queryKey: ['listing', routeSegment],
    queryFn: async () => {
      if (!routeSegment) return null;
      const slugNorm = routeSegment.trim().toLowerCase();

      // IMPORTANT:
      // When unauthenticated, attempting getDoc() on a non-existent document id can throw
      // permission-denied depending on rules evaluation. We prefer slug lookup first.
      try {
        const qSnap = await getDocs(
          query(
            collection(db, 'listings'),
            where('slug', '==', slugNorm),
            where('status', '==', 'published'),
            limit(1)
          )
        );
        if (!qSnap.empty) {
          const d = qSnap.docs[0];
          return { id: d.id, ...d.data() } as Listing;
        }
      } catch {
        // ignore and fall back to by-id read below
      }

      // Fallback: allow opening by document id (admins/hosts can view drafts via id URLs).
      try {
        const byId = await getDoc(doc(db, 'listings', routeSegment));
        if (byId.exists()) {
          return { id: byId.id, ...byId.data() } as Listing;
        }
      } catch {
        // ignore (e.g. permission-denied when not logged in)
      }

      return null;
    },
    enabled: !!routeSegment,
    staleTime: 0,
  });

  const isLoading = listingLoading || (!!routeSegment && !listingFetched);

  useLayoutEffect(() => {
    if (!routeSegment || isLoading) return;
    const pathOnly =
      typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search || ''}` : '/';
    if (!listing) {
      applyPageSeo({
        title: 'Listing not found',
        description: 'This holiday rental could not be found.',
        path: pathOnly,
        noIndex: true,
      });
      return;
    }
    const published = listing.status === 'published';
    const path = getListingPath(listing);
    const desc = stripHtmlForMeta(listing.description || '', 160);
    const fallbackDesc = `${listing.title} - ${listing.location?.city || ''}, ${listing.location?.country || 'South Africa'}`;
    const image = listing.coverImageUrl || listing.images?.[0];
    applyPageSeo({
      title: listing.title,
      description: desc || fallbackDesc,
      path,
      image,
      ogType: 'article',
      noIndex: !published,
      jsonLd: {
        '@type': 'VacationRental',
        name: listing.title,
        description: desc || fallbackDesc,
        image: (listing.images || []).filter(Boolean).slice(0, 12).map((u) => absoluteUrl(u)),
        url: absoluteUrl(path),
        address: {
          '@type': 'PostalAddress',
          streetAddress: listing.location?.address,
          addressLocality: listing.location?.city,
          addressRegion: listing.location?.region,
          addressCountry: listing.location?.country,
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: listing.location?.lat,
          longitude: listing.location?.lng,
        },
        numberOfRooms: listing.bedrooms,
      },
    });
  }, [listing, routeSegment, isLoading]);

  const listingId = listing?.id;

  // Fetch bookings for this listing (only for host/admin for calendar visuals)
  const { data: bookings } = useQuery({
    queryKey: ['bookings', listingId],
    queryFn: async () => {
      if (!listingId) return [];
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('listingId', '==', listingId),
        where('status', '==', 'confirmed')
      );
      const snapshot = await getDocs(bookingsQuery);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },
    enabled:
      !!listingId &&
      !!currentUser &&
      !!userData &&
      ['admin', 'manager', 'host'].includes(userData.role),
  });

  // Helper functions to check date availability
  const isDateBooked = (date: Date) => {
    if (!bookings || bookings.length === 0) return false;
    return bookings.some((booking: any) => {
      const bookingCheckIn = booking.checkinTimestamp?.toDate
        ? booking.checkinTimestamp.toDate()
        : new Date(booking.checkinTimestamp);
      const bookingCheckOut = booking.checkoutTimestamp?.toDate
        ? booking.checkoutTimestamp.toDate()
        : new Date(booking.checkoutTimestamp);
      const dateStr = date.toISOString().split('T')[0];
      const checkInStr = bookingCheckIn.toISOString().split('T')[0];
      const checkOutStr = bookingCheckOut.toISOString().split('T')[0];
      return dateStr >= checkInStr && dateStr < checkOutStr;
    });
  };

  const isDateBlocked = (date: Date) => {
    if (!listing) return false;
    const blockedDates = listing.calendar?.blocked || [];
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.includes(dateStr);
  };

  // Nightly price from date-range rules or base price
  const getNightPrice = useCallback(
    (date: Date) => (listing ? getNightlyPriceCents(listing, date) : 0),
    [listing]
  );

  const isDateAvailable = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate >= today && !isDateBooked(date) && !isDateBlocked(date);
  };

  // Auto-update price breakdown when dates change
  useEffect(() => {
    if (checkIn && checkOut && listing && checkIn < checkOut) {
      const breakdown = calculateStayBreakdown(listing, checkIn, checkOut);
      const { subtotal, nights, priceDetails, cleaningFee, serviceFee, taxes, total } = breakdown;

      // Quick validation
      if (nights < listing.minNights || nights > listing.maxNights) {
        setAvailability({ 
          available: false, 
          message: `Booking must be between ${listing.minNights} and ${listing.maxNights} nights` 
        });
        return;
      }

      // Check if any date in range is blocked or booked
      const dateRange = Array.from({ length: nights }, (_, i) => {
        const date = new Date(checkIn);
        date.setDate(date.getDate() + i);
        return date;
      });

      const hasBlockedOrBooked = dateRange.some(date => isDateBooked(date) || isDateBlocked(date));

      if (hasBlockedOrBooked) {
        setAvailability({ 
          available: false, 
          message: 'Selected dates include blocked or booked dates' 
        });
        return;
      }

      // Calculate price (per night may vary by priceRules)
      setAvailability({
        available: true,
        totalCents: total,
        breakdown: {
          basePrice: listing.basePrice || 0,
          nights,
          subtotal,
          cleaningFee,
          serviceFee,
          taxes,
          total,
          currency: 'ZAR',
          priceDetails,
        },
        message: 'Available',
      });
    } else if (checkIn && checkOut && checkIn >= checkOut) {
      setAvailability({ 
        available: false, 
        message: 'Check-out must be after check-in' 
      });
    } else {
      setAvailability(null);
    }
  }, [checkIn, checkOut, listing, bookings]);

  const checkAvailability = async () => {
    if (!checkIn || !checkOut || !listingId || !listing) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setCheckingAvailability(true);
    try {
      const checkInISO = checkIn.toISOString().split('T')[0];
      const checkOutISO = checkOut.toISOString().split('T')[0];
      const fn = httpsCallable(functions, 'checkAvailabilityFunction');
      const result = await fn({
        listingId: listingId,
        checkIn: checkInISO,
        checkOut: checkOutISO,
      });
      const data = result.data as {
        available: boolean;
        totalCents: number;
        breakdown?: any;
        message?: string;
      };
      if (!data.available) {
        toast.error(data.message || 'Selected dates are not available');
        setAvailability({
          available: false,
          message: data.message || 'Selected dates are not available',
        });
        setShowCalendar(true);
      } else {
        toast.success('Dates are available!');
        setAvailability({
          available: true,
          totalCents: data.totalCents,
          breakdown: data.breakdown,
          message: data.message || 'Available',
        });
      }
    } catch (error: any) {
      console.error('Availability check error:', error);
      toast.error(error.message || 'Error checking availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleEnquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!listingId || !listing) {
      toast.error('Listing not loaded yet');
      return;
    }
    if (!enquiryName || !enquiryEmail || !enquiryMessage) {
      toast.error('Please fill in your name, email, and message');
      return;
    }
    try {
      setSendingEnquiry(true);
      const fn = httpsCallable(functions, 'sendEnquiryEmailFunction');
      await fn({
        name: enquiryName,
        email: enquiryEmail,
        phone: enquiryPhone,
        message: enquiryMessage,
        listingId: listingId,
      });
      toast.success('Enquiry sent. The team will contact you shortly.');
      setEnquiryMessage('');
    } catch (error: any) {
      toast.error(error?.message || 'Could not send enquiry. Please try again.');
    } finally {
      setSendingEnquiry(false);
    }
  };

  const handleBook = async () => {
    if (!currentUser) {
      toast.error('Please log in to make a booking');
      navigate('/login');
      return;
    }

    if (!availability?.available || !checkIn || !checkOut || !listingId || !listing) {
      toast.error('Please check availability first and select dates');
      return;
    }

    try {
      const nights = Math.ceil(
        (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
      );

      const checkInISO = checkIn.toISOString().split('T')[0];
      const checkOutISO = checkOut.toISOString().split('T')[0];
      const fn = httpsCallable(functions, 'checkAvailabilityFunction');
      const result = await fn({
        listingId,
        checkIn: checkInISO,
        checkOut: checkOutISO,
      });
      const data = result.data as { available: boolean; message?: string };
      if (!data.available) {
        toast.error(data.message || 'These dates are no longer available. Please select different dates.');
        setAvailability(null);
        return;
      }

      const checkInTimestamp = Timestamp.fromDate(checkIn);
      const checkOutTimestamp = Timestamp.fromDate(checkOut);
      const bookingsRef = collection(db, 'bookings');

      const bookingData = {
        listingId: listingId,
        hostId: listing.hostId,
        guestId: currentUser.uid,
        checkinTimestamp: checkInTimestamp,
        checkoutTimestamp: checkOutTimestamp,
        nights,
        totalCents: availability.totalCents,
        currency: 'ZAR',
        status: 'pending' as const,
        payment: {
          amount: availability.totalCents,
          currency: 'ZAR',
          provider: 'payfast',
          captureStatus: 'pending',
        },
        cancellationPolicy: 'moderate',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(bookingsRef, bookingData);
      toast.success('Request received - we will get back to you within 48 hours.');
      navigate(`/booking/${docRef.id}/confirmation`);
    } catch (error: any) {
      console.error('Booking creation error:', error);
      if (error?.code === 'permission-denied') {
        toast.error('You do not have permission to create this booking. Please make sure you are logged in and try again.');
        if (!currentUser) {
          navigate('/login');
        }
      } else {
        toast.error(error?.message || 'Could not create booking. Please try again.');
      }
    }
  };

  if (
    params.listingSlug &&
    RESERVED_LISTING_SLUGS.has(params.listingSlug.toLowerCase())
  ) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-gray-600">Listing not found</p>
      </div>
    );
  }

  const priceDisplay = getListingPriceDisplay(listing);
  const sortedPriceRules = sortPriceRules(listing.priceRules || []);
  const hasSeasonalRates = sortedPriceRules.length > 0;

  const renderCalendarDay = (day: number, date: Date) => {
    const cents = getNightPrice(date);
    const priceLabel = `R ${Math.round(cents / 100)}`;

    return (
      <div
        className="calendar-day-cell"
        title={`${toDateStr(date)} — R ${(cents / 100).toFixed(2)} / night`}
      >
        <span className="calendar-day-number">{day}</span>
        <span className="calendar-day-price">{priceLabel}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title and Location Section */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 font-heading">{listing.title}</h1>
            <button
              type="button"
              onClick={async () => {
                const url = getListingPublicUrl(listing);
                if (!url) return;
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success('Link copied. Share this URL with your guest.');
                } catch {
                  toast.error('Could not copy. Copy from the address bar.');
                }
              }}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm font-medium hover:bg-gray-50"
            >
              <i className="uil uil-link text-lg" aria-hidden />
              Copy link
            </button>
          </div>
          <p className="text-lg text-gray-600 mb-5 flex items-center">
            <i className="uil uil-map-marker text-base mr-2 text-[#32373c]"></i>
            {formatListingLocationLine(listing)}
          </p>
          <div className="flex flex-wrap gap-2 mb-6">
            <div className="flex items-center bg-gray-100 px-4 py-1.5 rounded-full border border-gray-200">
              <i className="uil uil-bed-double text-base mr-1.5 text-[#32373c]"></i>
              <span className="text-sm font-medium text-gray-700">{listing.bedrooms} Bedrooms</span>
            </div>
            <div className="flex items-center bg-gray-100 px-4 py-1.5 rounded-full border border-gray-200">
              <i className="uil uil-bath text-base mr-1.5 text-[#32373c]"></i>
              <span className="text-sm font-medium text-gray-700">{listing.bathrooms} Bathrooms</span>
            </div>
            <div className="flex items-center bg-gray-100 px-4 py-1.5 rounded-full border border-gray-200">
              <i className="uil uil-users-alt text-base mr-1.5 text-[#32373c]"></i>
              <span className="text-sm font-medium text-gray-700">Sleeps {listing.capacity}</span>
            </div>
          </div>
        </div>

        {/* Image Gallery - Mobile: Grid, Desktop: Professional Carousel */}
        {listing.images && listing.images.length > 0 ? (
          <div className="mb-6">
            {/* Mobile View - Compact Grid Layout */}
            <div className="md:hidden">
              {listing.images.length === 1 ? (
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                  <img
                    src={listing.images[0]}
                    alt={listing.title}
                    className="w-full h-[300px] object-cover bg-gray-100"
                    onClick={() => {
                      setSelectedImageIndex(0);
                      setMobileGalleryOpen(true);
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/1200x800?text=Image+Not+Available';
                    }}
                  />
                </div>
              ) : listing.images.length === 2 ? (
                <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden">
                  {listing.images.map((image, idx) => (
                    <div key={idx} className="relative overflow-hidden bg-gray-100 rounded-lg">
                      <img
                        src={image}
                        alt={`${listing.title} ${idx + 1}`}
                        className="w-full h-[250px] object-cover"
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setMobileGalleryOpen(true);
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : listing.images.length === 3 ? (
                <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden">
                  <div className="row-span-2 relative overflow-hidden bg-gray-100 rounded-lg">
                    <img
                      src={listing.images[0]}
                      alt={`${listing.title} 1`}
                      className="w-full h-full min-h-[300px] object-cover"
                      onClick={() => {
                        setSelectedImageIndex(0);
                        setMobileGalleryOpen(true);
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x800?text=Image+Not+Available';
                      }}
                    />
                  </div>
                  {listing.images.slice(1, 3).map((image, idx) => (
                    <div key={idx + 1} className="relative overflow-hidden bg-gray-100 rounded-lg">
                      <img
                        src={image}
                        alt={`${listing.title} ${idx + 2}`}
                        className="w-full h-[145px] object-cover"
                        onClick={() => {
                          setSelectedImageIndex(idx + 1);
                          setMobileGalleryOpen(true);
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/600x400?text=Image+Not+Available';
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden">
                  {listing.images.slice(0, 4).map((image, idx) => (
                    <div key={idx} className="relative overflow-hidden bg-gray-100 rounded-lg">
                      <img
                        src={image}
                        alt={`${listing.title} ${idx + 1}`}
                        className="w-full h-[200px] object-cover"
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setMobileGalleryOpen(true);
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/600x400?text=Image+Not+Available';
                        }}
                      />
                      {idx === 3 && listing.images.length > 4 && (
                        <button
                          type="button"
                          className="absolute inset-0 bg-black/50 text-white font-semibold text-sm"
                          onClick={() => {
                            setSelectedImageIndex(0);
                            setMobileGalleryOpen(true);
                          }}
                        >
                          +{listing.images.length - 4} more
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile full-screen gallery */}
            {mobileGalleryOpen && (
              <div
                className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
                role="dialog"
                aria-modal="true"
              >
                <div className="flex items-center justify-between px-4 py-3 text-white">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2"
                    onClick={() => setMobileGalleryOpen(false)}
                  >
                    <i className="uil uil-times text-2xl" aria-hidden />
                    <span className="text-sm font-medium">Close</span>
                  </button>
                  <div className="text-sm font-medium">
                    {selectedImageIndex + 1} / {listing.images.length}
                  </div>
                </div>

                <div className="flex-1 flex items-center justify-center relative px-3">
                  {listing.images.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full p-3"
                        onClick={() =>
                          setSelectedImageIndex((prev) =>
                            prev === 0 ? listing.images.length - 1 : prev - 1
                          )
                        }
                        aria-label="Previous image"
                      >
                        <i className="uil uil-angle-left text-2xl" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full p-3"
                        onClick={() =>
                          setSelectedImageIndex((prev) =>
                            prev === listing.images.length - 1 ? 0 : prev + 1
                          )
                        }
                        aria-label="Next image"
                      >
                        <i className="uil uil-angle-right text-2xl" aria-hidden />
                      </button>
                    </>
                  )}

                  <img
                    src={listing.images[selectedImageIndex]}
                    alt={`${listing.title} ${selectedImageIndex + 1}`}
                    className="max-h-[75vh] w-auto object-contain rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        'https://via.placeholder.com/1200x800?text=Image+Not+Available';
                    }}
                  />
                </div>

                {listing.images.length > 1 && (
                  <div className="px-3 pb-4">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {listing.images.map((img, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 ${
                            idx === selectedImageIndex ? 'border-white' : 'border-white/20'
                          }`}
                          onClick={() => setSelectedImageIndex(idx)}
                          aria-label={`Go to image ${idx + 1}`}
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Desktop View - Professional Carousel */}
            <div className="hidden md:block relative">
              <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-lg bg-white">
                {/* Main carousel image container */}
                <div className="relative h-[450px] lg:h-[500px] flex items-center justify-center bg-gray-50">
                  <img
                    src={listing.images[selectedImageIndex]}
                    alt={`${listing.title} ${selectedImageIndex + 1}`}
                    className="max-w-full max-h-full w-auto h-auto object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/1200x800?text=Image+Not+Available';
                    }}
                  />
                  
                  {/* Navigation arrows */}
                  {listing.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setSelectedImageIndex((prev) => (prev === 0 ? listing.images.length - 1 : prev - 1))}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-50 text-gray-900 rounded-full p-3 shadow-xl border border-gray-200 transition-all z-10 group"
                        aria-label="Previous image"
                      >
                        <i className="uil uil-angle-left text-2xl group-hover:scale-110 transition-transform"></i>
                      </button>
                      <button
                        onClick={() => setSelectedImageIndex((prev) => (prev === listing.images.length - 1 ? 0 : prev + 1))}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-50 text-gray-900 rounded-full p-3 shadow-xl border border-gray-200 transition-all z-10 group"
                        aria-label="Next image"
                      >
                        <i className="uil uil-angle-right text-2xl group-hover:scale-110 transition-transform"></i>
                      </button>
                    </>
                  )}
                </div>
                
                {/* Bottom section with dots and thumbnail strip */}
                {listing.images.length > 1 && (
                  <div className="bg-white border-t border-gray-200 p-4">
                    {/* Dot indicators */}
                    <div className="flex justify-center gap-2 mb-3">
                      {listing.images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`transition-all rounded-full ${
                            selectedImageIndex === idx
                              ? 'bg-[#32373c] w-10 h-2'
                              : 'bg-gray-300 hover:bg-gray-400 w-2 h-2'
                          }`}
                          aria-label={`Go to image ${idx + 1}`}
                        />
                      ))}
                    </div>
                    
                    {/* Thumbnail strip */}
                    <div className="flex gap-3 justify-center overflow-x-auto pb-2 -mx-1 px-1">
                      {listing.images.map((image, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImageIndex === idx
                              ? 'border-[#32373c] ring-2 ring-[#32373c] ring-opacity-30 scale-105'
                              : 'border-gray-200 hover:border-gray-400 hover:scale-105'
                          }`}
                          aria-label={`View image ${idx + 1}`}
                        >
                          <img
                            src={image}
                            alt={`${listing.title} thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                    
                    {/* Image counter */}
                    <div className="text-center mt-3 text-sm text-gray-600 font-medium">
                      Image {selectedImageIndex + 1} of {listing.images.length}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 rounded-xl overflow-hidden border border-gray-200 bg-gray-100 h-[300px] md:h-[450px] flex items-center justify-center">
            <p className="text-gray-400">No images available</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">

            {/* About Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900 font-heading flex items-center">
                <i className="uil uil-info-circle text-xl mr-2 text-[#32373c]"></i>
                About this property
              </h2>
              {looksLikeHtml(listing.description || '') ? (
                <div
                  className="text-gray-700 leading-relaxed space-y-3 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
                  dangerouslySetInnerHTML={{ __html: listing.description }}
                />
              ) : (
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {listing.description}
                </p>
              )}
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
              <h2 className="text-2xl font-semibold mb-6 text-gray-900 font-heading flex items-center">
                <i className="uil uil-home text-xl mr-2 text-[#32373c]"></i>
                Property Details
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-300 transition-colors">
                  <i className="uil uil-bed-double text-2xl text-[#32373c] mb-2"></i>
                  <div className="text-2xl font-bold text-[#32373c] mb-1">{listing.bedrooms}</div>
                  <div className="text-xs text-gray-600 font-medium">Bedrooms</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-300 transition-colors">
                  <i className="uil uil-bath text-2xl text-[#32373c] mb-2"></i>
                  <div className="text-2xl font-bold text-[#32373c] mb-1">{listing.bathrooms}</div>
                  <div className="text-xs text-gray-600 font-medium">Bathrooms</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-300 transition-colors">
                  <i className="uil uil-users-alt text-2xl text-[#32373c] mb-2"></i>
                  <div className="text-2xl font-bold text-[#32373c] mb-1">{listing.capacity}</div>
                  <div className="text-xs text-gray-600 font-medium">Guests</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-300 transition-colors">
                  <i className="uil uil-moon text-2xl text-[#32373c] mb-2"></i>
                  <div className="text-2xl font-bold text-[#32373c] mb-1">{listing.minNights}</div>
                  <div className="text-xs text-gray-600 font-medium">Min Nights</div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            {listing.amenities && listing.amenities.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
                <h2 className="text-2xl font-semibold mb-5 text-gray-900 font-heading flex items-center">
                  <i className="uil uil-star text-xl mr-2 text-[#32373c]"></i>
                  Amenities
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {listing.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-100">
                      <i className="uil uil-check-circle text-lg text-green-600 mr-2"></i>
                      <span className="font-body text-gray-700 text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location Map */}
            {listing.location?.lat && listing.location?.lng && (
              <ListingLocationMap key={listing.id} listing={listing} />
            )}

            {/* Enquiry form */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
              <h2 className="text-2xl font-semibold mb-4 text-gray-900 font-heading flex items-center">
                <i className="uil uil-envelope text-xl mr-2 text-[#32373c]"></i>
                Enquire about this property
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Send a quick enquiry to check availability, ask a question, or request more
                information. The Leisure Breaks team will reply by email.
              </p>
              <form onSubmit={handleEnquirySubmit} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={enquiryName}
                      onChange={(e) => setEnquiryName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      className="input"
                      value={enquiryEmail}
                      onChange={(e) => setEnquiryEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    className="input"
                    value={enquiryPhone}
                    onChange={(e) => setEnquiryPhone(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    className="input"
                    rows={4}
                    value={enquiryMessage}
                    onChange={(e) => setEnquiryMessage(e.target.value)}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={sendingEnquiry}
                  className="w-full py-3 px-4 bg-[#32373c] hover:bg-[#2a2e33] text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
                >
                  {sendingEnquiry ? (
                    <>
                      <i className="uil uil-spinner-alt animate-spin text-base mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="uil uil-envelope-send text-base mr-2"></i>
                      Send enquiry
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-5 md:p-6 sticky top-6 border border-gray-200">
            <div className="mb-6 pb-5 border-b border-gray-200">
              <div className="text-3xl font-bold text-[#32373c] mb-1 font-heading">
                {priceDisplay.hasVariableRates ? (
                  <>
                    <span className="text-lg font-medium text-gray-600 mr-1">From</span>R{' '}
                    {priceDisplay.primary.split(' - ')[0]}
                  </>
                ) : (
                  <>R {priceDisplay.primary}</>
                )}
              </div>
              <div className="text-sm text-gray-600 flex items-center">
                <i className="uil uil-calendar-alt text-base mr-1.5"></i>
                {priceDisplay.hasVariableRates ? priceDisplay.secondary : 'per night'}
              </div>
            </div>

            {hasSeasonalRates && (
              <div className="mb-5 pb-5 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Seasonal rates</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex justify-between">
                    <span>Standard rate</span>
                    <span className="font-medium">R {((listing.basePrice || 0) / 100).toFixed(2)} / night</span>
                  </li>
                  {sortedPriceRules.map((rule) => (
                    <li key={rule.id} className="flex justify-between gap-3">
                      <span>
                        {formatPriceRuleLabel(rule)}
                        <span className="block text-xs text-gray-500">
                          {rule.startDate} to {rule.endDate}
                        </span>
                      </span>
                      <span className="font-medium whitespace-nowrap">
                        R {(rule.price / 100).toFixed(2)} / night
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-4 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <i className="uil uil-sign-in-alt text-base mr-1.5 text-[#32373c]"></i>
                  Check-in
                </label>
                <DatePicker
                  selected={checkIn}
                  onChange={(date) => {
                    setCheckIn(date);
                    if (date && checkOut && date >= checkOut) {
                      setCheckOut(null);
                    }
                    setAvailability(null);
                  }}
                  selectsStart
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={new Date()}
                  placeholderText="Check-in date"
                  className="input w-full"
                  calendarClassName="listing-pricing-calendar"
                  renderDayContents={renderCalendarDay}
                  filterDate={(date) => isDateAvailable(date)}
                  excludeDates={[
                    ...(bookings?.flatMap((booking: any) => {
                      const bookingCheckIn = booking.checkinTimestamp?.toDate
                        ? booking.checkinTimestamp.toDate()
                        : new Date(booking.checkinTimestamp);
                      const bookingCheckOut = booking.checkoutTimestamp?.toDate
                        ? booking.checkoutTimestamp.toDate()
                        : new Date(booking.checkoutTimestamp);
                      const days = Math.ceil(
                        (bookingCheckOut.getTime() - bookingCheckIn.getTime()) / (1000 * 60 * 60 * 24)
                      );
                      return Array.from({ length: days }, (_, i) => {
                        const d = new Date(bookingCheckIn);
                        d.setDate(d.getDate() + i);
                        return d;
                      });
                    }) || []),
                    ...(listing?.calendar?.blocked?.map((dateStr: string) => new Date(dateStr)) || [])
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <i className="uil uil-sign-out-alt text-base mr-1.5 text-[#32373c]"></i>
                  Check-out
                </label>
                <DatePicker
                  selected={checkOut}
                  onChange={(date) => {
                    setCheckOut(date);
                    setAvailability(null);
                  }}
                  selectsEnd
                  startDate={checkIn}
                  endDate={checkOut}
                  minDate={checkIn || new Date()}
                  placeholderText="Check-out date"
                  className="input w-full"
                  calendarClassName="listing-pricing-calendar"
                  renderDayContents={renderCalendarDay}
                  filterDate={(date) => {
                    if (!checkIn) return isDateAvailable(date);
                    // For check-out, ensure the entire range is available
                    const nights = Math.ceil(
                      (date.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
                    );
                    if (listing && (nights < listing.minNights || nights > listing.maxNights)) {
                      return false;
                    }
                    // Check all dates in the range
                    const dateRange = Array.from({ length: nights }, (_, i) => {
                      const d = new Date(checkIn);
                      d.setDate(d.getDate() + i);
                      return d;
                    });
                    return dateRange.every(d => isDateAvailable(d));
                  }}
                  excludeDates={[
                    ...(bookings?.flatMap((booking: any) => {
                      const bookingCheckIn = booking.checkinTimestamp?.toDate
                        ? booking.checkinTimestamp.toDate()
                        : new Date(booking.checkinTimestamp);
                      const bookingCheckOut = booking.checkoutTimestamp?.toDate
                        ? booking.checkoutTimestamp.toDate()
                        : new Date(booking.checkoutTimestamp);
                      const days = Math.ceil(
                        (bookingCheckOut.getTime() - bookingCheckIn.getTime()) / (1000 * 60 * 60 * 24)
                      );
                      return Array.from({ length: days }, (_, i) => {
                        const d = new Date(bookingCheckIn);
                        d.setDate(d.getDate() + i);
                        return d;
                      });
                    }) || []),
                    ...(listing?.calendar?.blocked?.map((dateStr: string) => new Date(dateStr)) || [])
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <i className="uil uil-users-alt text-base mr-1.5 text-[#32373c]"></i>
                  Guests
                </label>
                <input
                  type="number"
                  min="1"
                  max={listing.capacity}
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                  className="input"
                />
              </div>
            </div>

            {availability && availability.available && (
              <div className="mb-5 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-base font-semibold mb-4 text-gray-900 flex items-center">
                  <i className="uil uil-receipt text-lg mr-2 text-green-700"></i>
                  Price Breakdown
                </div>
                <div className="space-y-3 text-sm">
                  {availability.breakdown.priceDetails?.length > 1 &&
                    new Set(availability.breakdown.priceDetails.map((d: { price: number }) => d.price)).size >
                      1 && (
                      <div className="pb-2 mb-2 border-b border-green-200 space-y-1">
                        {availability.breakdown.priceDetails.map(
                          (detail: { date: string; price: number }) => (
                            <div key={detail.date} className="flex justify-between text-gray-600 text-xs">
                              <span>{detail.date}</span>
                              <span>R {(detail.price / 100).toFixed(2)}</span>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal ({availability.breakdown.nights} nights)</span>
                    <span className="font-semibold">R {(availability.breakdown.subtotal / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Cleaning fee</span>
                    <span className="font-semibold">R {(availability.breakdown.cleaningFee / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Service fee</span>
                    <span className="font-semibold">R {(availability.breakdown.serviceFee / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Taxes (VAT)</span>
                    <span className="font-semibold">R {(availability.breakdown.taxes / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-green-300 pt-2 mt-2 text-gray-900">
                    <span>Total</span>
                    <span className="text-[#32373c]">R {(availability.breakdown.total / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={checkAvailability}
              disabled={!checkIn || !checkOut || checkingAvailability}
              className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium rounded-lg transition-colors mb-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
            >
              {checkingAvailability ? (
                <>
                  <i className="uil uil-spinner-alt animate-spin text-base mr-2"></i>
                  Checking...
                </>
              ) : (
                <>
                  <i className="uil uil-search text-base mr-2"></i>
                  Check Availability
                </>
              )}
            </button>

            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="w-full py-2.5 px-4 bg-white border border-gray-300 hover:border-[#32373c] text-gray-900 font-medium rounded-lg transition-colors mb-3 flex items-center justify-center text-sm"
            >
              <i className={`uil ${showCalendar ? 'uil-eye-slash' : 'uil-calendar-alt'} text-base mr-2`}></i>
              {showCalendar ? 'Hide Calendar' : 'View Calendar'}
            </button>

            <button
              onClick={handleBook}
              disabled={!availability?.available || checkingAvailability}
              className="w-full py-3.5 px-4 bg-[#32373c] hover:bg-[#2a2e33] text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {availability?.available ? (
                <>
                  <i className="uil uil-check-circle text-base mr-2"></i>
                  Send booking request
                </>
              ) : (
                <>
                  <i className="uil uil-calendar-alt text-base mr-2"></i>
                  Select Dates
                </>
              )}
            </button>

            {availability && !availability.available && (
              <p className="mt-4 text-sm text-red-600 text-center">
                {availability.message || 'Please check availability first'}
              </p>
            )}
          </div>
        </div>

        {/* Availability Calendar */}
        {(showCalendar || (availability && !availability.available)) && (
          <div className="mt-8 lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 md:p-6">
              <h2 className="text-xl font-semibold mb-5 font-heading flex items-center">
                <i className="uil uil-calendar-alt text-lg mr-2 text-[#32373c]"></i>
                Availability Calendar
              </h2>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-100 border border-green-500 rounded mr-2"></div>
                <span className="text-xs font-medium text-gray-700">Available</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-100 border border-yellow-500 rounded mr-2"></div>
                <span className="text-xs font-medium text-gray-700">Blocked</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-100 border border-red-500 rounded mr-2"></div>
                <span className="text-xs font-medium text-gray-700">Booked</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-200 border border-gray-400 rounded mr-2"></div>
                <span className="text-xs font-medium text-gray-700">Past</span>
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <i className="uil uil-info-circle text-sm mr-1.5"></i>
                Hover a date to see the nightly rate
              </div>
            </div>

            {/* Calendar */}
            <div className="flex justify-center listing-pricing-calendar">
              <DatePicker
                selected={checkIn}
                onChange={(date) => {
                  setCheckIn(date);
                  setCheckOut(null);
                  setAvailability(null);
                }}
                inline
                minDate={new Date()}
                monthsShown={2}
                calendarClassName="listing-pricing-calendar"
                renderDayContents={renderCalendarDay}
                dayClassName={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const checkDate = new Date(date);
                  checkDate.setHours(0, 0, 0, 0);
                  
                  if (checkDate < today) {
                    return 'bg-gray-200 text-gray-400 cursor-not-allowed';
                  }
                  if (isDateBooked(date)) {
                    return 'bg-red-100 text-red-800 border-2 border-red-500 font-semibold';
                  }
                  if (isDateBlocked(date)) {
                    return 'bg-yellow-100 text-yellow-800 border-2 border-yellow-500 font-semibold';
                  }
                  return 'bg-green-50 text-green-800 border-2 border-green-500 hover:bg-green-100';
                }}
                filterDate={(date) => isDateAvailable(date)}
                excludeDates={[
                  ...(bookings?.flatMap((booking: any) => {
                    const bookingCheckIn = booking.checkinTimestamp?.toDate
                      ? booking.checkinTimestamp.toDate()
                      : new Date(booking.checkinTimestamp);
                    const bookingCheckOut = booking.checkoutTimestamp?.toDate
                      ? booking.checkoutTimestamp.toDate()
                      : new Date(booking.checkoutTimestamp);
                    const days = Math.ceil(
                      (bookingCheckOut.getTime() - bookingCheckIn.getTime()) / (1000 * 60 * 60 * 24)
                    );
                    return Array.from({ length: days }, (_, i) => {
                      const d = new Date(bookingCheckIn);
                      d.setDate(d.getDate() + i);
                      return d;
                    });
                  }) || []),
                  ...(listing?.calendar?.blocked?.map((dateStr: string) => new Date(dateStr)) || [])
                ]}
                highlightDates={bookings?.flatMap((booking: any) => {
                  const bookingCheckIn = booking.checkinTimestamp?.toDate
                    ? booking.checkinTimestamp.toDate()
                    : new Date(booking.checkinTimestamp);
                  const bookingCheckOut = booking.checkoutTimestamp?.toDate
                    ? booking.checkoutTimestamp.toDate()
                    : new Date(booking.checkoutTimestamp);
                  const days = Math.ceil(
                    (bookingCheckOut.getTime() - bookingCheckIn.getTime()) / (1000 * 60 * 60 * 24)
                  );
                  return Array.from({ length: days }, (_, i) => {
                    const d = new Date(bookingCheckIn);
                    d.setDate(d.getDate() + i);
                    return d;
                  });
                }) || []}
              />
            </div>

            {availability && !availability.available && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">{availability.message}</p>
                <p className="text-red-600 text-sm mt-2">
                  Please select available dates from the calendar above. Available dates are shown in green.
                </p>
              </div>
            )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

interface ListingLocationMapProps {
  listing: Listing;
}

function ListingLocationMap({ listing }: ListingLocationMapProps) {
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  // Do not call useLoadScript with '' – the library loads the demo key and throws InvalidKey.
  if (!googleMapsApiKey) return null;
  return <ListingLocationMapInner listing={listing} googleMapsApiKey={googleMapsApiKey} />;
}

function ListingLocationMapInner({
  listing,
  googleMapsApiKey,
}: ListingLocationMapProps & { googleMapsApiKey: string }) {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const advancedMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined;

  const center = useMemo(
    () => ({
      lat: listing.location.lat,
      lng: listing.location.lng,
    }),
    [listing.location.lat, listing.location.lng]
  );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries: MAP_LIBRARIES,
  });

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
  }, []);

  // Must run before any early return (Rules of Hooks). React #310 if placed after !isLoaded return.
  useEffect(() => {
    if (!mapId || !mapInstance) return;
    let cancelled = false;
    const map = mapInstance;
    (async () => {
      try {
        const { AdvancedMarkerElement } = (await google.maps.importLibrary(
          'marker'
        )) as google.maps.MarkerLibrary;
        if (cancelled) return;
        const marker = new AdvancedMarkerElement({ map, position: center });
        if (cancelled) {
          marker.map = null;
          return;
        }
        advancedMarkerRef.current = marker;
      } catch {
        // mapId invalid or marker library unavailable
      }
    })();
    return () => {
      cancelled = true;
      if (advancedMarkerRef.current) {
        advancedMarkerRef.current.map = null;
        advancedMarkerRef.current = null;
      }
    };
  }, [mapId, mapInstance, center.lat, center.lng]);

  const mapOptionsStable = useMemo(() => {
    const o: google.maps.MapOptions = {
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    };
    if (mapId) o.mapId = mapId;
    return o;
  }, [mapId]);

  if (loadError) return null;

  if (!isLoaded) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900 font-heading flex items-center">
          <i className="uil uil-map-marker text-xl mr-2 text-[#32373c]"></i>
          Location
        </h2>
        <p className="text-gray-600 text-sm">Loading map…</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 font-heading flex items-center">
        <i className="uil uil-map-marker text-xl mr-2 text-[#32373c]"></i>
        Location
      </h2>
      <p className="text-gray-700 mb-4">{formatListingLocationLine(listing)}</p>
      <div className="w-full h-64 rounded-xl overflow-hidden border border-gray-200">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={14}
          onLoad={onMapLoad}
          options={mapOptionsStable}
        >
          {!mapId && <Marker position={center} />}
        </GoogleMap>
      </div>
    </div>
  );
}


